import {ColumnDescription} from './ColumnDescription';
import {asString} from '../../../../../common/helpers/converters/asString';
import {IMG_1x1_URL} from '../../../../../common/CONST';
import {TableHeaderCell} from '@sabre/spark-react-core';
import {TableSortOrder} from '@sabre/spark-react-core/table/table.types';
import React, {KeyboardEvent} from 'react';
import {getColumnClassName} from './helpers/getColumnClassName';
import {getColumnStyle} from './helpers/getColumnStyle';
import {SortDirection} from './helpers/sorting';
import {tAsString} from '../../../../../common/helpers/react/text/tAsString';

export type DefaultTableHeaderCellProps<T> = {
    column: ColumnDescription<T>,
    sortDirection?: SortDirection,
    onSort?: () => void
}

export default function DefaultTableHeaderCell<T>(props: DefaultTableHeaderCellProps<T>) {
    const c = props.column;

    const isSortable = !!c.sortable && !!props.onSort;

    const baseAriaLabel = c.ariaLabel
        ?? c.header
        ?? asString(c.name);

    const header = c.renderHeader?.(c)
        ?? c.header
        ?? <img
            aria-hidden={true}
            src={IMG_1x1_URL}
            alt={baseAriaLabel}
        />;

    const ariaLabel = isSortable
        ? tAsString('SORTABLE_COLUMN_X', {
            name: baseAriaLabel,
            state: tAsString(
                props.sortDirection === 'asc'
                    ? 'SORTED_ASCENDING'
                    : props.sortDirection === 'desc'
                        ? 'SORTED_DESCENDING'
                        : 'NOT_SORTED'
            )
        })
        : baseAriaLabel;

    const headerCellProps = c.headerCellProps?.(c);

    const sortOrder = isSortable
        ? props.sortDirection === 'asc'
            ? TableSortOrder.ASC
            : props.sortDirection === 'desc'
                ? TableSortOrder.DESC
                : TableSortOrder.NONE
        : TableSortOrder.NO_SORT;

    const ariaSort: 'ascending' | 'descending' | 'none' | undefined = isSortable
        ? props.sortDirection === 'asc'
            ? 'ascending'
            : props.sortDirection === 'desc'
                ? 'descending'
                : 'none'
        : undefined;

    const sortHandlers = isSortable
        ? {
            onClick: () => props.onSort?.(),
            onKeyDown: (event: KeyboardEvent<HTMLTableCellElement>) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    props.onSort?.();
                }
            }
        }
        : {};

    const newHeaderCellProps = {
        ...headerCellProps,
        ...sortHandlers,
        className: getColumnClassName(
            c,
            headerCellProps?.className
        ),
        style: getColumnStyle(
            c,
            {
                verticalAlign: 'middle',
                ...(isSortable ? {cursor: 'pointer'} : {}),
                ...headerCellProps?.style
            }
        )
    };

    return <TableHeaderCell
        aria-label={ariaLabel !== header ? ariaLabel : undefined}
        aria-sort={ariaSort}
        sort={sortOrder}
        {...newHeaderCellProps}
    >{header}</TableHeaderCell>;
}
